import { useState, type FormEvent, type KeyboardEvent } from 'react'
import { ArrowLeft, ArrowRight, CalendarDays, CheckCircle2, KeyRound, Loader2, Mail, MapPin, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router'
import { EVENT } from '../config/event'
import '../styles/login-concepts-v3.css'

type LoginConceptV3Variant = 'transit' | 'batik' | 'archive' | 'risograph' | 'broadcast'
type Stage = 'email' | 'code' | 'password' | 'success'

interface ConceptSpec {
  number: string
  name: string
  label: string
  eyebrow: string
  headline: string
  accentWord: string
  description: string
  formTitle: string
  formSubtitle: string
  note: string
}

const CONCEPTS: Record<LoginConceptV3Variant, ConceptSpec> = {
  transit: {
    number: '11',
    name: 'Transit Board',
    label: 'Platform 01 / Access',
    eyebrow: 'CPD CENTRAL / DELEGATE WAYFINDING',
    headline: 'Your next stop is CPD.',
    accentWord: 'next stop',
    description: 'A departure-board approach for delegates who need one immediate answer: where do I go next?',
    formTitle: 'Check in at platform 01',
    formSubtitle: 'Enter your registration email to receive your access route.',
    note: 'Transit board · platform blue · signal orange',
  },
  batik: {
    number: '12',
    name: 'Batik Signal',
    label: 'Motif / Continuity',
    eyebrow: 'CPD UiTM / PROFESSIONAL PRACTICE SERIES',
    headline: 'Learning is a pattern you keep.',
    accentWord: 'pattern you keep',
    description: 'A contemporary pattern-led identity where repetition signals progress, continuity and a shared professional craft.',
    formTitle: 'Continue the pattern',
    formSubtitle: 'Use the email connected to your CPD registration.',
    note: 'Contemporary batik geometry · indigo · clay · saffron',
  },
  archive: {
    number: '13',
    name: 'Museum Archive',
    label: 'Collection / Current',
    eyebrow: 'CPD COLLECTIONS / UiTM KNOWLEDGE ARCHIVE',
    headline: 'A record worth returning to.',
    accentWord: 'record worth',
    description: 'An archival catalogue treatment that frames attendance, learning records and certificates as a collection you can revisit.',
    formTitle: 'Open your record',
    formSubtitle: 'Your registered email locates the right collection.',
    note: 'Catalogue card · archival cream · oxblood reference mark',
  },
  risograph: {
    number: '14',
    name: 'Riso Poster',
    label: 'Ink / In progress',
    eyebrow: 'CPD 2026 / IDEAS IN REGISTRATION',
    headline: 'Make room for the next idea.',
    accentWord: 'next idea',
    description: 'A two-ink poster language with deliberate registration offsets: warm, optimistic and unmistakably made for an event.',
    formTitle: 'Print your entry',
    formSubtitle: 'Email first; your access method follows.',
    note: 'Risograph overlap · vermilion · ultramarine · paper white',
  },
  broadcast: {
    number: '15',
    name: 'Broadcast Desk',
    label: 'Live / Ready',
    eyebrow: 'CPD LIVE DESK / PROGRAMME CONTROL',
    headline: 'You’re cleared for the next session.',
    accentWord: 'cleared',
    description: 'A compact live-desk interface that makes the event feel active without burying the login behind a dashboard.',
    formTitle: 'Join the live desk',
    formSubtitle: 'Authenticate to see your programme and attendance status.',
    note: 'Broadcast control · monitor green · tungsten amber',
  },
}

const CONCEPT_ROUTES: Array<{ variant: LoginConceptV3Variant; path: string }> = [
  { variant: 'transit', path: '/preview/login/v3/transit' },
  { variant: 'batik', path: '/preview/login/v3/batik' },
  { variant: 'archive', path: '/preview/login/v3/archive' },
  { variant: 'risograph', path: '/preview/login/v3/risograph' },
  { variant: 'broadcast', path: '/preview/login/v3/broadcast' },
]

export function getLoginConceptV3FromPath(pathname: string): LoginConceptV3Variant | null {
  return CONCEPT_ROUTES.find((route) => route.path === pathname)?.variant ?? null
}

function ConceptVisual({ variant }: { variant: LoginConceptV3Variant }) {
  if (variant === 'transit') {
    return (
      <div className="lc3__transit-board" aria-hidden="true">
        <div className="lc3__board-label"><span>CPD CENTRAL</span><b>Departures</b></div>
        <div className="lc3__departure lc3__departure--active"><span>01</span><b>YOUR PROGRAMME</b><em>NOW</em></div>
        <div className="lc3__departure"><span>02</span><b>SESSION HALL</b><em>09:30</em></div>
        <div className="lc3__departure"><span>03</span><b>CERTIFICATE DESK</b><em>LATER</em></div>
      </div>
    )
  }
  if (variant === 'batik') {
    return (
      <div className="lc3__batik-motif" aria-hidden="true">
        <span /><span /><span /><span /><span /><span /><span /><span /><span />
      </div>
    )
  }
  if (variant === 'archive') {
    return (
      <div className="lc3__catalogue-card" aria-hidden="true">
        <span className="lc3__catalogue-stamp">OPEN<br />COLLECTION</span>
        <div><small>OBJECT 13 / CPD 2026</small><b>Attendance<br />&amp; learning</b></div>
        <span className="lc3__catalogue-line" />
        <small>ACCESSION 08—26—UITM</small>
      </div>
    )
  }
  if (variant === 'risograph') {
    return (
      <div className="lc3__riso-poster" aria-hidden="true">
        <div className="lc3__riso-ring lc3__riso-ring--red" />
        <div className="lc3__riso-ring lc3__riso-ring--blue" />
        <strong>MAKE<br />SPACE</strong>
        <span>FOR<br />IDEAS</span>
      </div>
    )
  }
  return (
    <div className="lc3__broadcast-panel" aria-hidden="true">
      <div className="lc3__broadcast-head"><span><i /> LIVE PROGRAMME</span><b>15:42:08</b></div>
      <div className="lc3__meter"><span /><span /><span /><span /><span /><span /><span /><span /><span /><span /></div>
      <div className="lc3__broadcast-lines"><b>ON AIR</b><span>HALL A / SESSION 04</span><em>READY</em></div>
    </div>
  )
}

export function LoginConceptsV3Route({ variant }: { variant: LoginConceptV3Variant }) {
  const spec = CONCEPTS[variant]
  const [stage, setStage] = useState<Stage>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState<string[]>(['', '', '', '', '', ''])
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  function submitEmail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!email.trim() || busy) return
    setBusy(true)
    window.setTimeout(() => {
      setBusy(false)
      setStage(email.toLowerCase().includes('staff') || email.toLowerCase().includes('admin') ? 'password' : 'code')
    }, 420)
  }

  function submitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (code.join('').length !== 6 || busy) return
    setBusy(true)
    window.setTimeout(() => {
      setBusy(false)
      setStage('success')
    }, 520)
  }

  function submitPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!password.trim() || busy) return
    setBusy(true)
    window.setTimeout(() => {
      setBusy(false)
      setStage('success')
    }, 520)
  }

  function restart() {
    setStage('email')
    setCode(['', '', '', '', '', ''])
    setPassword('')
  }

  function updateDigit(index: number, rawValue: string) {
    const digits = rawValue.replace(/\D/g, '').slice(0, 6)
    const next = [...code]
    if (!digits) {
      next[index] = ''
      setCode(next)
      return
    }
    digits.split('').forEach((digit, offset) => {
      if (index + offset < next.length) next[index + offset] = digit
    })
    setCode(next)
    document.getElementById(`concept-v3-otp-${Math.min(index + digits.length, 5)}`)?.focus()
  }

  function handleDigitKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace' && !code[index] && index > 0) document.getElementById(`concept-v3-otp-${index - 1}`)?.focus()
    if (event.key === 'ArrowLeft' && index > 0) document.getElementById(`concept-v3-otp-${index - 1}`)?.focus()
    if (event.key === 'ArrowRight' && index < 5) document.getElementById(`concept-v3-otp-${index + 1}`)?.focus()
  }

  const heading = stage === 'email' ? spec.formTitle : stage === 'code' ? 'Check your inbox' : stage === 'password' ? 'Staff access' : 'Access confirmed'
  const subtitle = stage === 'email' ? spec.formSubtitle : stage === 'code' ? 'Enter the six-digit code we sent you.' : stage === 'password' ? 'Enter the password for this venue account.' : 'Your conference dashboard is ready.'
  const accentIndex = spec.headline.indexOf(spec.accentWord)
  const beforeAccent = accentIndex >= 0 ? spec.headline.slice(0, accentIndex) : spec.headline
  const afterAccent = accentIndex >= 0 ? spec.headline.slice(accentIndex + spec.accentWord.length) : ''

  return (
    <main className={`lc3 lc3--${variant}`}>
      <div className="lc3__texture" aria-hidden="true" />
      <header className="lc3__topbar">
        <Link className="lc3__brand" to="/login" aria-label="CPD 2026 live sign-in"><span>CPD</span><b>2026</b></Link>
        <div className="lc3__series">NEW DIRECTIONS / {spec.number}</div>
        <Link className="lc3__live-link" to="/login">Live sign-in <ArrowRight size={14} aria-hidden="true" /></Link>
      </header>

      <nav className="lc3__switcher" aria-label="New CPD login concepts">
        <span className="lc3__switcher-label">Set three</span>
        {CONCEPT_ROUTES.map((route) => (
          <Link key={route.variant} className={route.variant === variant ? 'lc3__switcher-link lc3__switcher-link--active' : 'lc3__switcher-link'} to={route.path}>
            <span>{CONCEPTS[route.variant].number}</span>{CONCEPTS[route.variant].name}
          </Link>
        ))}
      </nav>

      <div className="lc3__layout">
        <section className="lc3__story" aria-labelledby="concept-v3-headline">
          <div className="lc3__story-topline"><span className="lc3__index">{spec.number}</span><span>{spec.eyebrow}</span></div>
          <div className="lc3__story-copy">
            <p className="lc3__event-mark">{spec.label} <span aria-hidden="true">↗</span></p>
            <h1 id="concept-v3-headline" className="lc3__headline">{beforeAccent}<em>{spec.accentWord}</em>{afterAccent}</h1>
            <p className="lc3__description">{spec.description}</p>
          </div>
          <ConceptVisual variant={variant} />
          <dl className="lc3__event-meta">
            <div><dt><CalendarDays size={14} aria-hidden="true" /> Dates</dt><dd>{EVENT.dateLabel}</dd></div>
            <div><dt><MapPin size={14} aria-hidden="true" /> Venue</dt><dd>{EVENT.venue}</dd></div>
          </dl>
          <p className="lc3__design-note">{spec.note}</p>
        </section>

        <section className="lc3__panel" aria-label="Preview sign in">
          <div className="lc3__panel-heading" aria-live="polite"><p className="lc3__panel-kicker">{stage === 'success' ? 'ACCESS CONFIRMED' : 'PARTICIPANT ACCESS'}</p><h2>{heading}</h2><p>{subtitle}</p></div>
          {stage === 'email' ? (
            <form className="lc3__form" onSubmit={submitEmail}>
              <label className="lc3__field-label" htmlFor="concept-v3-email">Email address</label>
              <div className="lc3__input-wrap"><Mail size={18} aria-hidden="true" /><input id="concept-v3-email" type="email" inputMode="email" autoComplete="email" placeholder="name@uitm.edu.my" value={email} onChange={(event) => setEmail(event.target.value)} required /></div>
              <p className="lc3__hint">We’ll identify whether this address uses a one-time code or staff password.</p>
              <button className="lc3__primary" type="submit" disabled={busy || !email.trim()}>{busy ? <Loader2 className="lc3__spin" size={18} aria-label="Checking" /> : <>Continue <ArrowRight size={17} aria-hidden="true" /></>}</button>
            </form>
          ) : null}
          {stage === 'code' ? (
            <form className="lc3__form" onSubmit={submitCode}>
              <div className="lc3__addressed"><span>Code sent to</span><strong>{email.trim()}</strong><button type="button" onClick={restart}>Change</button></div>
              <fieldset className="lc3__otp-fieldset"><legend>Six-digit verification code</legend><div className="lc3__otp-row">{code.map((digit, index) => <input key={index} id={`concept-v3-otp-${index}`} className="lc3__otp" aria-label={`Digit ${index + 1} of 6`} type="text" inputMode="numeric" maxLength={6} value={digit} onChange={(event) => updateDigit(index, event.target.value)} onKeyDown={(event) => handleDigitKeyDown(index, event)} autoFocus={index === 0} />)}</div></fieldset>
              <p className="lc3__hint">For this visual preview, any six digits complete the sign-in.</p><button className="lc3__primary" type="submit" disabled={busy || code.join('').length !== 6}>{busy ? <Loader2 className="lc3__spin" size={18} aria-label="Verifying" /> : <>Verify and continue <ArrowRight size={17} aria-hidden="true" /></>}</button><button className="lc3__back" type="button" onClick={restart}><ArrowLeft size={14} aria-hidden="true" /> Use a different address</button>
            </form>
          ) : null}
          {stage === 'password' ? (
            <form className="lc3__form" onSubmit={submitPassword}>
              <div className="lc3__addressed"><span>Staff account</span><strong>{email.trim()}</strong><button type="button" onClick={restart}>Change</button></div>
              <label className="lc3__field-label" htmlFor="concept-v3-password">Password</label><div className="lc3__input-wrap"><KeyRound size={18} aria-hidden="true" /><input id="concept-v3-password" type="password" autoComplete="current-password" placeholder="Enter staff password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus required /></div>
              <button className="lc3__primary" type="submit" disabled={busy || !password.trim()}>{busy ? <Loader2 className="lc3__spin" size={18} aria-label="Signing in" /> : <>Sign in <ArrowRight size={17} aria-hidden="true" /></>}</button><button className="lc3__back" type="button" onClick={restart}><ArrowLeft size={14} aria-hidden="true" /> Use a different address</button>
            </form>
          ) : null}
          {stage === 'success' ? <div className="lc3__success"><CheckCircle2 size={46} aria-hidden="true" /><p className="lc3__success-title">Signed in successfully</p><p>Your programme, attendance and certificate status are ready.</p><button className="lc3__primary" type="button" onClick={restart}>Preview again <ArrowRight size={17} aria-hidden="true" /></button></div> : null}
          <footer className="lc3__panel-footer"><ShieldCheck size={15} aria-hidden="true" /> Secure, encrypted login</footer>
        </section>
      </div>

      <footer className="lc3__footer"><span>Set three · design comparison only · no real sign-in occurs here</span><Link to="/login">Return to live CPD sign-in</Link></footer>
    </main>
  )
}
